import React, { useEffect, useMemo, useState } from "react";
import PreviewControls from "../preview/PreviewControls";
import ResponsivePreview from "../preview/ResponsivePreview";
import PreviewRenderer from "../preview/PreviewRenderer";
import PreviewErrorBoundary from "../preview/PreviewErrorBoundary";

function groupByCategory(components) {
  return components.reduce((acc, component) => {
    const key = component.category || "General";
    if (!acc[key]) acc[key] = [];
    acc[key].push(component);
    return acc;
  }, {});
}

const SCENARIO_STATUS_CLASSES = {
  success: "bg-emerald-50 text-emerald-700",
  failed: "bg-rose-50 text-rose-700",
  skipped: "bg-slate-100 text-slate-600"
};

const ComponentLibrary = () => {
  const [components, setComponents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewState, setPreviewState] = useState({});

  useEffect(() => {
    let mounted = true;
    async function run() {
      setIsLoading(true);
      try {
        const response = await fetch("/generated/component-index.json");
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = await response.json();
        if (mounted) {
          setComponents(payload.components ?? []);
          const nextPreviewState = {};
          (payload.components ?? []).forEach((component) => {
            const firstScenario = component.scenarioDetails?.[0];
            nextPreviewState[component.id] = {
              scenarioId: firstScenario?.id ?? "",
              viewport: firstScenario?.viewport ?? "desktop"
            };
          });
          setPreviewState(nextPreviewState);
          setError("");
        }
      } catch (err) {
        if (mounted) {
          setError(`Kunne ikke laste komponentindeks: ${err.message}`);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, []);

  const grouped = useMemo(() => groupByCategory(components), [components]);

  const setScenarioFor = (componentId, scenarioId) => {
    setPreviewState((prev) => ({
      ...prev,
      [componentId]: {
        ...(prev[componentId] ?? {}),
        scenarioId
      }
    }));
  };

  const setViewportFor = (componentId, viewport) => {
    setPreviewState((prev) => ({
      ...prev,
      [componentId]: {
        ...(prev[componentId] ?? {}),
        viewport
      }
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-900">Komponentbibliotek (pilot)</h1>
          <p className="mt-2 text-sm text-slate-600">
            Pilotvisning for registrerte komponenter i Promang Frontend. Portalen foretrekker
            interaktiv live-preview i isolert iframe når preview-host er konfigurert, og bruker
            genererte snapshot som fallback og CI-grunnlag.
          </p>
        </header>

        {isLoading && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">
            Laster komponentindeks...
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800">{error}</div>
        )}

        {!isLoading && !error && (
          <div className="space-y-6">
            {Object.entries(grouped).map(([category, items]) => (
              <section key={category} className="space-y-3">
                <h2 className="text-lg font-semibold text-slate-800">{category}</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {items.map((component) => (
                    <article
                      key={component.id}
                      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">{component.title}</h3>
                          <p className="mt-1 text-sm text-slate-600">{component.description || "Ingen beskrivelse."}</p>
                        </div>
                        <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                          {component.framework}
                        </span>
                      </div>

                      <dl className="mt-3 grid grid-cols-1 gap-1 text-xs text-slate-500">
                        <div>
                          <dt className="inline font-semibold text-slate-600">Dokument:</dt>{" "}
                          <dd className="inline">{component.documentId}</dd>
                        </div>
                        <div>
                          <dt className="inline font-semibold text-slate-600">Kilde:</dt>{" "}
                          <dd className="inline">{component.sourcePath}</dd>
                        </div>
                        <div>
                          <dt className="inline font-semibold text-slate-600">Wrappers:</dt>{" "}
                          <dd className="inline">{(component.wrappers || []).join(", ") || "standard"}</dd>
                        </div>
                      </dl>

                      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <h4 className="text-sm font-semibold text-slate-800">Scenarioer</h4>
                        <ul className="mt-2 space-y-1 text-sm text-slate-700">
                          {(component.scenarioDetails || []).map((scenario) => (
                            <li key={scenario.id} className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{scenario.title}</span>
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  SCENARIO_STATUS_CLASSES[scenario.status] ?? SCENARIO_STATUS_CLASSES.skipped
                                }`}
                              >
                                {scenario.status ?? "skipped"}
                              </span>
                              <span className="ml-2 text-xs text-slate-500">
                                ({scenario.wrapper}, {scenario.viewport})
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {component.scenarioDetails?.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <PreviewControls
                            scenarios={component.scenarioDetails}
                            selectedScenarioId={previewState[component.id]?.scenarioId}
                            onScenarioChange={(scenarioId) => setScenarioFor(component.id, scenarioId)}
                            selectedViewport={previewState[component.id]?.viewport ?? "desktop"}
                            onViewportChange={(viewport) => setViewportFor(component.id, viewport)}
                          />

                          <PreviewErrorBoundary
                            componentTitle={component.title}
                            scenarioId={previewState[component.id]?.scenarioId}
                            wrapper={
                              component.scenarioDetails.find(
                                (scenario) => scenario.id === previewState[component.id]?.scenarioId
                              )?.wrapper ?? "standard"
                            }
                          >
                            <ResponsivePreview viewport={previewState[component.id]?.viewport ?? "desktop"}>
                              <PreviewRenderer
                                componentId={component.id}
                                scenario={
                                  component.scenarioDetails.find(
                                    (scenario) => scenario.id === previewState[component.id]?.scenarioId
                                  ) ?? component.scenarioDetails[0]
                                }
                              />
                            </ResponsivePreview>
                          </PreviewErrorBoundary>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentLibrary;
